import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { rem } from 'polished'
import type { WrappedComponentProps } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'

import partnershipRewardMessage from 'lib/partnership/partnershipRewardMessage'

import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { addGTMEvent } from 'api/googleTagManager'
import { promotionBanner } from 'analytics/promotions'

const PartnershipBannerPane = styled(Pane)`
  width: 100%;
  padding: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const BannerContent = styled(PaneBody)`
  display: flex;
  align-items: center;
  justify-content: center;

  > * + * {
    margin-left: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    > * + * {
      margin-left: ${rem(16)};
    }
  }
`

const StyledImage = styled(ResponsiveImage)`
  max-width: ${rem(135)};
`

interface OwnProps {
  bannerId: string
}

interface StateProps {
  partnership: App.Partnership | undefined
}

type Props = OwnProps & StateProps & WrappedComponentProps

class PartnershipBannerContainer extends Component<React.PropsWithChildren<Props>> {
  trackContentImpression = () => {
    addGTMEvent(promotionBanner({ bannerId: this.props.bannerId, hitType: 'promotion-impression' }))
  }

  onClickTrack = () => {
    addGTMEvent(promotionBanner({ bannerId: this.props.bannerId, hitType: 'promotion-click' }))
  }

  render() {
    const {
      partnership,
      intl,
    } = this.props

    if (!partnership) {
      return null
    }

    const burnMessage = intl.formatMessage({
      id: 'VWNITkZyIb',
      defaultMessage: ' OR use your {rewardName} to put towards your next escape',
      description: 'Partnership banner text for using reward points',
    }, { rewardName: partnership.rewardName })

    const earnMessage = intl.formatMessage({
      id: '5UPKlDWpZT',
      defaultMessage: '. Link your {programName} account to earn {rewardName} on eligible bookings ',
      description: 'Partnership banner text for linking account to earn rewards',
    }, { programName: partnership.programName, rewardName: partnership.rewardName })

    return <PartnershipBannerPane>
      <BannerContent size="md-y">
        <StyledImage
          loading="lazy"
          mobileWidth="100px"
          tabletWidth="135px"
          format="png"
          id={partnership.programLogo.id}
          alt={intl.formatMessage({
            id: '4AdagwlxXF',
            defaultMessage: '{programName} Logo',
            description: 'Alt text for partnership program logo',
          }, { programName: partnership.programName })}
          onLoad={this.trackContentImpression}
        />
        <BodyText variant="medium">
          <FormattedMessage
            id="gU0T/+Bqru"
            defaultMessage="Earn {rewardMessage}"
            description="Partnership banner earn rewards text"
            values={{ rewardMessage: partnershipRewardMessage(partnership) }}
          />
          {partnership.hasBurn ? burnMessage : earnMessage}{' '}
          <TextLink
            onClick={this.onClickTrack}
            to={`/${partnership.landingPage}`}
          >
            <FormattedMessage
              id="hovTH07J7v"
              defaultMessage="Learn more"
              description="Link text to learn more about partnership program"
            />
          </TextLink>
        </BodyText>
      </BannerContent>
    </PartnershipBannerPane>
  }
}

const mapStateToProps = (state: App.State): StateProps => ({
  partnership: state.geo.partnership,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(injectIntl(PartnershipBannerContainer))
